import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { MdOutlineInsertComment } from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  JOB_LIST,
  MEMBERSHIP_ORIGINS,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Information = ({ bindField, initialValues, setPercent, isFieldRequired }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const INFORMATION = t(
    "SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.INFORMATION",
    { returnObjects: true },
  );

  const membershipOriginPath = "project.contact.fields.membershipOrigin";
  const membershipOriginField = bindField(membershipOriginPath, {
    label: INFORMATION.MEMBERSHIP_ORIGIN,
    required: isFieldRequired(membershipOriginPath),
    onChange: () => setPercent(initialValues),
  });

  const offerCdsPath = "project.fields.info.offerCDS";
  const offerCdsField = bindField(offerCdsPath, {
    label: INFORMATION.CDS_OFFER,
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const politicallyExposedPath = "project.contact.politicallyExposedPerson";
  const politicallyExposedField = bindField(politicallyExposedPath, {
    label: INFORMATION.POLITICALLY_EXPOSED_PERSON.LABEL,
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const optMadelinPath = "project.contact.optMadelin";
  const optMadelinField = bindField(optMadelinPath, {
    label: INFORMATION.MADELIN,
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const acceptProductDocumentsPath = "project.fields.acceptProductDocuments";
  const acceptProductDocumentsField = bindField(acceptProductDocumentsPath, {
    label: INFORMATION.SO_LYON.DOCUMENT_INFOS,
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const acceptProductStatusesPath = "project.fields.acceptProductStatuses";
  const acceptProductStatusesField = bindField(acceptProductStatusesPath, {
    label: INFORMATION.SO_LYON.STATUSES_AGREEMENT,
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const acceptFeesPath = "project.fields.acceptFees";
  const acceptFeesField = bindField(acceptFeesPath, {
    label: INFORMATION.SO_LYON.FEES,
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const acceptNoticePath = "project.fields.acceptNotice";
  const acceptNoticeField = bindField(acceptNoticePath, {
    label: INFORMATION.SO_LYON.NOTICE,
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const acceptTeletransmissionPath = "project.fields.acceptTeletransmission";
  const acceptTeletransmissionField = bindField(acceptTeletransmissionPath, {
    label: INFORMATION.SO_LYON.TELETRANSMISSION,
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const acceptAgreementPath = "project.fields.acceptAgreement";
  const acceptAgreementField = bindField(acceptAgreementPath, {
    label: INFORMATION.SO_LYON.AGREEMENT,
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const commentPath = "project.fields.info.comment";
  const commentField = bindField(commentPath, {
    label: INFORMATION.COMMENT,
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const DEFAULT_QUESTIONS = [
    membershipOriginPath,
    offerCdsPath,
    politicallyExposedPath,
    ![
      JOB_LIST.HOSPITAL_CIVIL_SERVICE,
      JOB_LIST.HOSPITAL_PRACTITIONER,
      JOB_LIST.RETIRED,
    ].includes(get(initialValues, "project.contact.profession"))
      ? optMadelinPath
      : null,
    acceptProductDocumentsPath,
    acceptProductStatusesPath,
    acceptFeesPath,
    acceptNoticePath,
    acceptTeletransmissionPath,
    acceptAgreementPath,
  ];

  const QUESTIONS = {
    [membershipOriginPath]: (
      <FormField
        path={membershipOriginField.path}
        label={membershipOriginField.label}
        required={membershipOriginField.required}
        disabled={membershipOriginField.disabled}
      >
        {(field) => (
          <Select
            options={Object.entries(MEMBERSHIP_ORIGINS).map(([key, value]) => ({
              value,
              label: INFORMATION.MEMBERSHIP_ORIGINS[key],
            }))}
            label={membershipOriginField.label}
            placeholder={INFORMATION.MEMBERSHIP_ORIGIN}
            value={get(initialValues, membershipOriginPath)}
            {...field}
            onChange={membershipOriginField.onChange}
            onBlur={membershipOriginField.onBlur}
          />
        )}
      </FormField>
    ),
    [offerCdsPath]: (
      <FormField
        path={offerCdsField.path}
        label={offerCdsField.label}
        required={offerCdsField.required}
        disabled={offerCdsField.disabled}
      >
        {(field) => (
          <Input
            type="switch"
            label={INFORMATION.CDS_OFFER}
            checked={get(initialValues, offerCdsPath) === true}
            {...field}
            onChange={offerCdsField.onChange}
            onBlur={offerCdsField.onBlur}
          />
        )}
      </FormField>
    ),
    [politicallyExposedPath]: (
      <FormField
        path={politicallyExposedField.path}
        label={politicallyExposedField.label}
        required={politicallyExposedField.required}
        disabled={politicallyExposedField.disabled}
      >
        {(field) => (
          <Input
            type="switch"
            label={INFORMATION.POLITICALLY_EXPOSED_PERSON.LABEL}
            checked={get(initialValues, politicallyExposedPath) === true}
            {...field}
            onChange={politicallyExposedField.onChange}
            onBlur={politicallyExposedField.onBlur}
          />
        )}
      </FormField>
    ),
    [optMadelinPath]: (
      <FormField
        path={optMadelinField.path}
        label={optMadelinField.label}
        required={optMadelinField.required}
        disabled={optMadelinField.disabled}
      >
        {(field) => (
          <Input
            type="switch"
            label={INFORMATION.MADELIN}
            checked={get(initialValues, optMadelinPath) === true}
            {...field}
            onChange={optMadelinField.onChange}
            onBlur={optMadelinField.onBlur}
          />
        )}
      </FormField>
    ),
    [acceptProductDocumentsPath]: (
      <FormField
        path={acceptProductDocumentsField.path}
        label={acceptProductDocumentsField.label}
        required={acceptProductDocumentsField.required}
        disabled={acceptProductDocumentsField.disabled}
      >
        {(field) => (
          <Input
            type="switch"
            label={INFORMATION.SO_LYON.DOCUMENT_INFOS}
            checked={get(initialValues, acceptProductDocumentsPath) === true}
            {...field}
            onChange={acceptProductDocumentsField.onChange}
            onBlur={acceptProductDocumentsField.onBlur}
          />
        )}
      </FormField>
    ),
    [acceptProductStatusesPath]: (
      <FormField
        path={acceptProductStatusesField.path}
        label={acceptProductStatusesField.label}
        required={acceptProductStatusesField.required}
        disabled={acceptProductStatusesField.disabled}
      >
        {(field) => (
          <Input
            type="switch"
            label={INFORMATION.SO_LYON.STATUSES_AGREEMENT}
            checked={get(initialValues, acceptProductStatusesPath) === true}
            {...field}
            onChange={acceptProductStatusesField.onChange}
            onBlur={acceptProductStatusesField.onBlur}
          />
        )}
      </FormField>
    ),
    [acceptFeesPath]: (
      <FormField
        path={acceptFeesField.path}
        label={acceptFeesField.label}
        required={acceptFeesField.required}
        disabled={acceptFeesField.disabled}
      >
        {(field) => (
          <Input
            type="switch"
            label={INFORMATION.SO_LYON.FEES}
            checked={get(initialValues, acceptFeesPath) === true}
            {...field}
            onChange={acceptFeesField.onChange}
            onBlur={acceptFeesField.onBlur}
          />
        )}
      </FormField>
    ),
    [acceptNoticePath]: (
      <FormField
        path={acceptNoticeField.path}
        label={acceptNoticeField.label}
        required={acceptNoticeField.required}
        disabled={acceptNoticeField.disabled}
      >
        {(field) => (
          <Input
            type="switch"
            label={INFORMATION.SO_LYON.NOTICE}
            checked={get(initialValues, acceptNoticePath) === true}
            {...field}
            onChange={acceptNoticeField.onChange}
            onBlur={acceptNoticeField.onBlur}
          />
        )}
      </FormField>
    ),
    [acceptTeletransmissionPath]: (
      <FormField
        path={acceptTeletransmissionField.path}
        label={acceptTeletransmissionField.label}
        required={acceptTeletransmissionField.required}
        disabled={acceptTeletransmissionField.disabled}
      >
        {(field) => (
          <Input
            type="switch"
            label={INFORMATION.SO_LYON.TELETRANSMISSION}
            checked={get(initialValues, acceptTeletransmissionPath) === true}
            {...field}
            onChange={acceptTeletransmissionField.onChange}
            onBlur={acceptTeletransmissionField.onBlur}
          />
        )}
      </FormField>
    ),
    [acceptAgreementPath]: (
      <FormField
        path={acceptAgreementField.path}
        label={acceptAgreementField.label}
        required={acceptAgreementField.required}
        disabled={acceptAgreementField.disabled}
      >
        {(field) => (
          <Input
            type="switch"
            label={INFORMATION.SO_LYON.AGREEMENT}
            checked={get(initialValues, acceptAgreementPath) === true}
            {...field}
            onChange={acceptAgreementField.onChange}
            onBlur={acceptAgreementField.onBlur}
          />
        )}
      </FormField>
    ),
  };

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION}-section`}
    >
      <Form type="vertical" title={INFORMATION.TITLE}>
        <div className="grid--1">
          {DEFAULT_QUESTIONS.filter((entry) => entry != null).map((info) => (
            <div key={info}>{QUESTIONS[info]}</div>
          ))}
          <FormField
            path={commentField.path}
            label={commentField.label}
            required={commentField.required}
            disabled={commentField.disabled}
          >
            {(field) => (
              <Input
                type="textarea"
                label={INFORMATION.COMMENT}
                placeholder={INFORMATION.COMMENT}
                icon={<MdOutlineInsertComment size={16} />}
                normalizeText={true}
                defaultValue={commentField.defaultValue}
                {...field}
                onChange={commentField.onChange}
                onBlur={commentField.onBlur}
                autoSize={{ minRows: 2, maxRows: 10 }}
                optional={!isFieldRequired(commentPath)}
              />
            )}
          </FormField>
        </div>
      </Form>
    </div>
  );
};

export default Information;
